import React, { useEffect, useRef, useState } from 'react'

import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Alert, Autocomplete } from '@material-ui/lab'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core'
import {
  Add as AddIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  GetApp as DownloadIcon,
} from '@material-ui/icons'
import clsx from 'clsx'

import { BasePhoneInput } from 'components'
import useFetch from 'hooks/useFetch'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'
import { getBaseURL } from 'service/env'
import useStyles from '../styles'

import helpers from 'helpers'

const TEMPLATE_URL = `${getBaseURL('trainingPortal').replace(
  '/api/v1/portal',
  '',
)}/modelo_importacao_alunos.xlsx`

const DEFAULT_VALUES = {
  name: '',
  email: '',
  phone: '',
  deadline: moment().add(30, 'days'),
  notificationDate: moment(),
}

const LinkStudentDialog = ({ open, courseId, onClose, onSuccess }) => {
  const classes = useStyles()
  const snackbar = useSnackbar()
  const fileInputRef = useRef(null)

  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [dragOver, setDragOver] = useState(false)
  const [importFile, setImportFile] = useState(null)
  const [userSearchInput, setUserSearchInput] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedCollaborators, setSelectedCollaborators] = useState([])
  const [groupDepartments, setGroupDepartments] = useState([])
  const [groupProfiles, setGroupProfiles] = useState([])

  const { control, handleSubmit, watch, reset } = useForm({
    defaultValues: DEFAULT_VALUES,
  })

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(userSearchInput), 400)
    return () => clearTimeout(timer)
  }, [userSearchInput])

  const { response: collaboratorsResponse, loading: collaboratorsLoading } =
    useFetch(
      service.dponet.trainingEnrollment.companyUsers,
      { name: debouncedSearch, perPage: 20 },
      [debouncedSearch],
      activeTab === 2 && debouncedSearch.length >= 2,
    )

  const { response: departmentsResponse } = useFetch(
    service.dponet.departments.get,
    { perPage: 10000 },
    [],
    open,
  )

  const { response: profilesResponse } = useFetch(
    service.dponet.profiles.get,
    { perPage: 10000 },
    [],
    open,
  )

  const departmentOptions = departmentsResponse?.data?.departments ?? []
  const profileOptions = profilesResponse?.data?.profiles ?? []
  const collaboratorOptions = collaboratorsResponse?.data?.users ?? []

  const handleFileSelect = (file) => {
    if (file && file.name.endsWith('.xlsx')) {
      setImportFile(file)
    } else {
      snackbar.open({
        message: 'Apenas arquivos XLSX são aceitos.',
        variant: 'error',
      })
    }
  }

  const handleDrop = (event) => {
    event.preventDefault()
    setDragOver(false)
    handleFileSelect(event.dataTransfer.files[0])
  }

  const handleClose = () => {
    setActiveTab(0)
    setImportFile(null)
    setUserSearchInput('')
    setDebouncedSearch('')
    setSelectedCollaborators([])
    setGroupDepartments([])
    setGroupProfiles([])
    reset(DEFAULT_VALUES)
    onClose()
  }

  const handleEnrollCollaborators = async () => {
    setLoading(true)
    try {
      await service.dponet.trainingEnrollment.enrollByGroup({
        courseId,
        userIds: selectedCollaborators.map((collaborator) => collaborator.id),
        departmentIds: groupDepartments.map((department) => department.id),
        profileIds: groupProfiles.map((profile) => profile.id),
      })
      snackbar.open({
        message: 'Colaboradores vinculados ao curso com sucesso.',
        variant: 'success',
      })
      handleClose()
      onSuccess()
    } catch (error) {
      snackbar.open({
        message:
          helpers.formatters.errorMessage(error?.response?.data?.error) ||
          'Erro ao vincular colaboradores ao curso.',
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleImport = async () => {
    setLoading(true)
    try {
      await service.dponet.trainingEnrollment.importStudents({
        courseId,
        file: importFile,
      })
      snackbar.open({
        message:
          'Importação iniciada. Você receberá um e-mail com o resultado.',
        variant: 'info',
      })
      handleClose()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            `Ocorreu um erro ao tentar importar alunos`,
        ),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const email = watch('email')

  const onSubmit = async (data) => {
    setLoading(true)
    try {
      await service.dponet.trainingEnrollment.create({
        courseId,
        enrollment: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          deadline: data.deadline ? data.deadline.format('YYYY-MM-DD') : '',
          notificationDate: data.notificationDate
            ? data.notificationDate.format('YYYY-MM-DD')
            : '',
        },
      })
      onSuccess()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(
          error?.response?.data?.error ||
            `Occoreu um erro ao cadatra o aluno ${email}`,
        ),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  const phone = watch('phone')
  const hasEmail = !!email?.trim()
  const hasPhone = phone?.trim().length > 8
  const isImportTab = activeTab === 1
  const isCollaboratorsTab = activeTab === 2
  const hasCollaboratorSelection =
    selectedCollaborators.length > 0 ||
    groupDepartments.length > 0 ||
    groupProfiles.length > 0

  const canSubmit = isImportTab
    ? !!importFile
    : isCollaboratorsTab
    ? hasCollaboratorSelection
    : hasEmail || hasPhone

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AddIcon fontSize="small" />
            Gerenciar Alunos
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          <Tab label="Vincular Aluno" />
          <Tab label="Importação" />
          <Tab label="Colaboradores" />
        </Tabs>

        {activeTab === 0 && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="name"
                  control={control}
                  defaultValue=""
                  as={<TextField label="Nome" variant="outlined" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="phone"
                  control={control}
                  defaultValue=""
                  as={
                    <BasePhoneInput
                      label="Telefone"
                      variant="outlined"
                      fullWidth
                      helperText="Adicione o código do país e o DDD do seu telefone"
                    />
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  as={<TextField label="E-mail" variant="outlined" fullWidth />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="notificationDate"
                  control={control}
                  defaultValue={null}
                  as={
                    <KeyboardDatePicker
                      label="Notificar em"
                      format="DD/MM/yyyy"
                      fullWidth
                      inputVariant="outlined"
                      variant="inline"
                      clearable
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="deadline"
                  control={control}
                  defaultValue={null}
                  as={
                    <KeyboardDatePicker
                      label="Prazo para visualização"
                      format="DD/MM/yyyy"
                      fullWidth
                      inputVariant="outlined"
                      variant="inline"
                      clearable
                    />
                  }
                />
              </Grid>
            </Grid>
          </>
        )}

        {activeTab === 2 && (
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <Alert severity="warning" className={classes.alertBox}>
              Isso não é um cadastro de usuários, caso seus colaboradores não
              estejam cadastrados na plataforma serão redirecionados para um
              login externo.
            </Alert>
            <Autocomplete
              multiple
              options={collaboratorOptions}
              getOptionLabel={(option) => `${option.name} — ${option.email}`}
              getOptionSelected={(option, value) => option.id === value.id}
              value={selectedCollaborators}
              onChange={(event, newValue) => setSelectedCollaborators(newValue)}
              filterOptions={(options) => options}
              loading={collaboratorsLoading}
              onInputChange={(event, newInputValue) =>
                setUserSearchInput(newInputValue)
              }
              noOptionsText={
                debouncedSearch.length < 2
                  ? 'Digite ao menos 2 caracteres para buscar'
                  : 'Nenhum colaborador encontrado'
              }
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Buscar colaborador"
                  variant="outlined"
                  fullWidth
                  placeholder="Buscar por nome..."
                  InputProps={{
                    ...inputProps.InputProps,
                    endAdornment: (
                      <>
                        {collaboratorsLoading && <CircularProgress size={16} />}
                        {inputProps.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Autocomplete
              multiple
              options={departmentOptions}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              value={groupDepartments}
              onChange={(event, newValue) => setGroupDepartments(newValue)}
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Departamentos"
                  variant="outlined"
                  fullWidth
                  placeholder="Selecione os departamentos..."
                />
              )}
            />
            <Autocomplete
              multiple
              options={profileOptions}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              value={groupProfiles}
              onChange={(event, newValue) => setGroupProfiles(newValue)}
              renderInput={(inputProps) => (
                <TextField
                  {...inputProps}
                  label="Perfis"
                  variant="outlined"
                  fullWidth
                  placeholder="Selecione os perfis..."
                />
              )}
            />
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              style={{ marginBottom: 12 }}
            >
              <Typography variant="body2" color="textSecondary">
                Para importar, siga o modelo de planilha disponibilizado. Caso
                não seja seguido, podem ocorrer falhas na importação.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                href={TEMPLATE_URL}
                download
                style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
              >
                Baixar Modelo
              </Button>
            </Box>

            <Box
              className={clsx(classes.dropzone, {
                [classes.dropzoneActive]: dragOver,
              })}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault()
                setDragOver(true)
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
            >
              <CloudUploadIcon
                color="action"
                style={{ fontSize: 40, marginBottom: 8 }}
              />
              <Typography variant="body2">
                {importFile
                  ? importFile.name
                  : 'Arraste e solte ou selecione o documento'}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Arquivos suportados: XLSX
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(event) => handleFileSelect(event.target.files[0])}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={
            isImportTab
              ? handleImport
              : isCollaboratorsTab
              ? handleEnrollCollaborators
              : handleSubmit(onSubmit)
          }
          variant="contained"
          color="primary"
          disabled={loading || !canSubmit}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {isImportTab ? 'Importar' : 'Vincular'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default LinkStudentDialog
